import { useMemo } from 'react';
import { SWRConfiguration, mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { fetcher } from '../../../data/fetcher';
import { useStoredOrganizationId } from '../../../hooks/useStoredOrganizationId';
import {
  UseSWRMutationOptions,
  useSWR,
  useSWRMutation,
} from '../../../hooks/useSwr';
import { useOrganizationId } from '../../../hooks/useSwr';
import {
  CreateSessionClusterJobRequest,
  FetchClusterJobRunsResult,
  GetClusterJobRunsQueryParams,
  GetClusterJobsQueryParams,
  GetSessionClusterJobsResponse,
  GetSessionClusterResponse,
  SessionClusterJob,
  SessionClusterSchema,
  UpdateSessionClusterJobRequest,
  UpdateSessionClusterJobResponse,
} from '../../../types/schema';
import { apiSessionsPath } from '../../../utils/constants';

export function useSessionClusterJobs(
  queryParams?: GetClusterJobsQueryParams | null,
  options?: SWRConfiguration
) {
  return useSWR<GetSessionClusterJobsResponse>(
    queryParams !== null
      ? {
          url: `${apiSessionsPath}/${queryParams?.session_id}/insights${
            queryParams?.limit ? `?limit=${queryParams?.limit}` : ''
          }${queryParams?.offset ? `&offset=${queryParams?.offset}` : ''}`,
        }
      : null,
    options
  );
}

export function useSessionClusterJob(
  sessionId: string | null,
  clusterJobId: string | null,
  options?: SWRConfiguration
) {
  return useSWR<SessionClusterJob>(
    sessionId && clusterJobId
      ? {
          url: `${apiSessionsPath}/${sessionId}/insights/${clusterJobId}`,
        }
      : null,
    options
  );
}

export function useSessionCluster(
  sessionId: string | null,
  clusterJobId: string | null,
  clusterId: string | null,
  options?: SWRConfiguration
) {
  return useSWR<GetSessionClusterResponse>(
    sessionId && clusterJobId && clusterId
      ? {
          url: `${apiSessionsPath}/${sessionId}/insights/${clusterJobId}/clusters/${clusterId}`,
        }
      : null,
    options
  );
}

export function useClusterJobRuns(
  sessionId: string | null,
  clusterJobId: string | null,
  clusterId?: string | null,
  options?: { limit?: number; offset?: number },
  swrOptions?: SWRConfiguration
) {
  const url = useMemo(() => {
    if (!sessionId || !clusterJobId) return null;
    return `${apiSessionsPath}/${sessionId}/insights/${clusterJobId}/runs`;
  }, [sessionId, clusterJobId]);

  return useSWR<FetchClusterJobRunsResult, GetClusterJobRunsQueryParams>(
    url
      ? {
          url,
          params: {
            ...options,
            cluster_id: clusterId || undefined,
          },
        }
      : null,
    swrOptions
  );
}

export function useClusterJobRunsInfinite(
  sessionId: string | null,
  clusterJobId: string | null,
  clusterId?: string | null,
  options?: { limit?: number },
  swrOptions?: SWRConfiguration
) {
  const PAGE_SIZE = options?.limit ?? 30;
  const organizationId = useOrganizationId();
  const { orgId } = useStoredOrganizationId();

  const getKey = (
    pageIndex: number,
    previousPageData: FetchClusterJobRunsResult | null
  ) => {
    if (!sessionId || !clusterJobId) return null;

    if (pageIndex === 0) {
      const params: GetClusterJobRunsQueryParams = {
        limit: PAGE_SIZE,
        offset: 0,
      };
      if (clusterId) {
        params.cluster_id = clusterId;
      }
      return {
        url: `${apiSessionsPath}/${sessionId}/insights/${clusterJobId}/runs`,
        params,
        headers: { 'X-Tenant-Id': organizationId, 'X-Organization-Id': orgId },
      };
    }

    if (
      !previousPageData ||
      previousPageData.offset === null ||
      previousPageData.offset === undefined
    ) {
      return null;
    }

    const params: GetClusterJobRunsQueryParams = {
      limit: PAGE_SIZE,
      offset: previousPageData.offset,
    };
    if (clusterId) {
      params.cluster_id = clusterId;
    }
    return {
      url: `${apiSessionsPath}/${sessionId}/insights/${clusterJobId}/runs`,
      params,
      headers: { 'X-Tenant-Id': organizationId, 'X-Organization-Id': orgId },
    };
  };

  return useSWRInfinite<FetchClusterJobRunsResult>(getKey, fetcher, swrOptions);
}

export function useSessionClusterJobsMutation(
  sessionId: string,
  options?: UseSWRMutationOptions<
    SessionClusterSchema,
    CreateSessionClusterJobRequest
  >
) {
  return useSWRMutation<SessionClusterSchema, CreateSessionClusterJobRequest>(
    {
      url: `${apiSessionsPath}/${sessionId}/insights`,
      method: 'POST',
    },
    {
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        return mutate(
          (key) =>
            typeof key === 'object' &&
            key !== null &&
            'url' in key &&
            typeof key.url === 'string' &&
            key.url.startsWith(`${apiSessionsPath}/${sessionId}/insights`)
        );
      },
    }
  );
}

export function useSessionClusterJobUpdateMutation(
  sessionId: string,
  clusterJobId: string,
  options?: UseSWRMutationOptions<
    UpdateSessionClusterJobResponse,
    UpdateSessionClusterJobRequest
  >
) {
  return useSWRMutation<
    UpdateSessionClusterJobResponse,
    UpdateSessionClusterJobRequest
  >(
    {
      url: `${apiSessionsPath}/${sessionId}/insights/${clusterJobId}`,
      method: 'PATCH',
    },
    {
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        return Promise.all([
          mutate(`${apiSessionsPath}/${sessionId}/insights`),
          mutate(`${apiSessionsPath}/${sessionId}/insights/${clusterJobId}`),
        ]);
      },
    }
  );
}
